import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Download Excel file from backend export endpoint
 */
export const downloadExcel = async (endpoint, filename) => {
  try {
    const response = await fetch(`${API}/export/${endpoint}`);
    if (!response.ok) throw new Error('Export failed');
    
    const blob = await response.blob();
    saveAs(blob, filename || `export_${Date.now()}.xlsx`);
    return true;
  } catch (error) {
    console.error('Excel export error:', error);
    throw error;
  }
};

/**
 * Export DOM element to PDF
 */
export const exportToPDF = async (elementId, filename, options = {}) => {
  const element = document.getElementById(elementId);
  if (!element) {
    throw new Error(`Element with id "${elementId}" not found`);
  }

  const {
    orientation = 'landscape',
    scale = 2,
    backgroundColor = '#0a0a0b'
  } = options;

  try {
    // Capture the element as canvas
    const canvas = await html2canvas(element, {
      scale,
      backgroundColor,
      useCORS: true,
      allowTaint: true,
      logging: false,
      windowWidth: element.scrollWidth,
      windowHeight: element.scrollHeight
    });

    // Calculate dimensions
    const imgWidth = canvas.width;
    const imgHeight = canvas.height;
    
    // Create PDF with appropriate size
    const pdf = new jsPDF({
      orientation,
      unit: 'px',
      format: [imgWidth + 40, imgHeight + 40]
    });

    // Add image to PDF
    const imgData = canvas.toDataURL('image/png');
    pdf.addImage(imgData, 'PNG', 20, 20, imgWidth, imgHeight);

    // Save PDF
    pdf.save(filename || `export_${Date.now()}.pdf`);
    return true;
  } catch (error) {
    console.error('PDF export error:', error);
    throw error;
  }
};

/**
 * Export data to Excel on client side
 */
export const exportDataToExcel = (data, columns, filename, sheetName = 'Data') => {
  try {
    // Create worksheet data
    const wsData = [
      columns.map(col => col.header),
      ...data.map(row => columns.map(col => {
        const value = row[col.key];
        if (col.format === 'currency') {
          return typeof value === 'number' ? value : 0;
        }
        if (col.format === 'percent') {
          return typeof value === 'number' ? value / 100 : 0;
        }
        return value ?? '';
      }))
    ];

    // Create worksheet
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Set column widths
    ws['!cols'] = columns.map(() => ({ wch: 15 }));

    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, sheetName);

    // Generate file and download
    XLSX.writeFile(wb, filename);
    return true;
  } catch (error) {
    console.error('Client Excel export error:', error);
    throw error;
  }
};

/**
 * Format timestamp for filenames
 */
export const getTimestamp = () => {
  const now = new Date();
  return now.toISOString().replace(/[:.]/g, '-').slice(0, 19);
};
